/**
 * Resolves the currently-active weight vector.
 *
 * Fetches the latest version of every named vector (``only_latest=true``)
 * and returns whichever one has ``is_active = true``. Falls back to the
 * first vector in the list if none is explicitly active (e.g. on a fresh
 * install before the operator has activated anything).
 *
 * Results are cached for 60 s so every page that calls this hook shares
 * the same cached response without hammering the API.
 */
import { useQuery } from "@tanstack/react-query";
import { listWeightVectors, type WeightVectorOut } from "@/lib/api";

export function useActiveWeightVector(): {
  activeVector: WeightVectorOut | undefined;
  activeId: number | undefined;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ["weight-vectors", "active"],
    queryFn: async () => {
      const vecs = await listWeightVectors({ onlyLatest: true });
      return vecs.find((v) => v.is_active) ?? vecs[0] ?? null;
    },
    staleTime: 60_000,
  });

  return {
    activeVector: query.data ?? undefined,
    activeId: query.data?.id,
    isLoading: query.isLoading,
  };
}
